import { Segmented } from 'antd';
import { POST_COPY } from './copy';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [C1f §1a] NAME, OR DELEGATE — per post, and only while the post is untouched.
 *
 *  ⚠️ IT REPLACES A TEXT LINK NOBODY FOUND. [C1b] shipped "Split across units" as an
 *  underlined caption beneath the addressee codes of every allocation row, and the reported
 *  defect is that operators do not see it. A two-way control on the post's own header states
 *  the choice as a choice.
 *
 *  ⚠️ IT IS OFFERED ONLY WHILE THE POST IS EMPTY — no names AND no delegations (§1b,
 *  `postIsUnrouted`). That is STRICTER than the service, which happily splits an unfilled
 *  remainder; the model is unchanged and the UI simply never asks for a partial split.
 *
 *  ⚠️ AND IT IS ABSENT, NEVER DISABLED, FOR A UNIT NODAL (§1c). A disabled control is a
 *  promise that something could be done here; a unit has nothing beneath it to delegate to,
 *  so there is nothing to promise. `mayDelegateAllocation` is the predicate and the caller
 *  applies it — this component never learns whose screen it is on, exactly as `FillLines`
 *  does not.
 *
 *  ⚠️ A `Segmented`, WHICH IS THIS REPOSITORY'S TWO-VALUE SWITCH (`PostTypes`,
 *  `VehicleTypes`). It renders as radios, so jsdom can address both arms by name, and it adds
 *  no overlay — `census.spec.ts` counts `Dropdown`, `Tooltip`, `Drawer`, `Modal`, `Sheet` and
 *  `Dialog`, and this is none of them.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export type PostMode = 'name' | 'delegate';

export type PostModeControlProps = {
  postLabel: string;
  value: PostMode;
  onChange: (mode: PostMode) => void;
  'data-testid'?: string;
};

export const PostModeControl = ({ postLabel, value, onChange, 'data-testid': testId }: PostModeControlProps) => (
  <Segmented<PostMode>
    size="small"
    value={value}
    data-testid={testId}
    /*
     * ⚠️ THE ACCESSIBLE NAME NAMES THE POST, because a sheet renders one of these per post and
     * "Name / Delegate" alone would be several identically-named controls on one screen —
     * `pwa`'s tab rule, one app over.
     */
    aria-label={POST_COPY.modeLabel(postLabel)}
    options={[
      { label: POST_COPY.name, value: 'name' },
      { label: POST_COPY.delegate, value: 'delegate' },
    ]}
    onChange={onChange}
  />
);
